import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { useTranslation } from 'react-i18next';
import { ClassStatusBadge } from './ClassStatusBadge';

export interface TimetableEntry {
  schedule_id?: string;
  class_id?: string;
  class_name: string;
  class_type?: string;
  location_name?: string;
  day_of_week?: string;
  start_time?: string;
  end_time?: string;
  coaches?: string[];
  is_closed?: boolean;
  closed_reason?: string | null;
  is_public_holiday?: boolean;
  override_start?: string | null;
  override_end?: string | null;
  exception_id?: string | null;
}

interface ClassCardProps {
  entry: TimetableEntry;
  showBadges?: boolean;
  variant?: 'default' | 'compact';
  targetDate?: Date;
}

export function ClassCard({ entry, showBadges = true, variant = 'default', targetDate }: ClassCardProps) {
  const { t } = useTranslation();
  const colors = useAppColors();

  const isClosed = entry.is_closed;
  const isPublicHoliday = entry.is_public_holiday;
  const isOverride = !!(entry.override_start && entry.override_end);

  const startFormatted = (entry.override_start || entry.start_time || '').slice(0, 5);
  const endFormatted = (entry.override_end || entry.end_time || '').slice(0, 5);

  // Format date as "Feb 20" style
  const formatDateLabel = (date: Date): string => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  if (variant === 'compact') {
    return (
      <View
        style={[
          styles.compactCard,
          {
            backgroundColor: colors.card,
            borderColor: colors.border,
            opacity: isClosed ? 0.55 : 1,
          },
        ]}
      >
        <View style={styles.compactRow}>
          <View style={[styles.timeBox, { backgroundColor: colors.border }]}>
            <Text style={[styles.timeLabel, { color: colors.textSecondary }]}>
              {targetDate ? formatDateLabel(targetDate) : t('home.today')}
            </Text>
            <Text style={[styles.timeValue, { color: colors.text }]}>
              {startFormatted || '--:--'}
            </Text>
            {isClosed && (
              <Text style={[styles.closedLabel, { color: colors.textSecondary }]}>
                {isPublicHoliday ? t('classes.publicHolidayBadge') : t('classes.closedBadge')}
              </Text>
            )}
          </View>
          <View style={styles.compactInfo}>
            <Text style={[styles.compactClassName, { color: colors.text }]}>
              {t(entry.class_name, { defaultValue: entry.class_name }) || 'Class'}
            </Text>
            <View style={styles.metaRow}>
              <Icon name="ninja" size={12} color={colors.textSecondary} />
              <Text style={[styles.metaText, { color: colors.textSecondary }]}>
                {(entry.coaches || []).length > 0
                  ? t('home.coachPrefix', { name: entry.coaches![0] })
                  : t('home.coachTba')}
              </Text>
            </View>
            <View style={styles.metaRow}>
              <Icon name="map-marker" size={12} color={colors.textSecondary} />
              <Text style={[styles.metaText, { color: colors.textSecondary }]}>
                {t(entry.location_name || '', { defaultValue: entry.location_name }) || t('home.mainMat')}
              </Text>
            </View>
            {showBadges && (
              <ClassStatusBadge
                isClosed={isClosed}
                isPublicHoliday={isPublicHoliday}
                closedReason={entry.closed_reason}
                isTimeChanged={isOverride}
                compact
              />
            )}
          </View>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.classCard,
        {
          backgroundColor: colors.card,
          borderColor: colors.border,
          opacity: isClosed ? 0.55 : 1,
        },
      ]}
    >
      <View style={styles.classCardLeft}>
        <Text style={[styles.classTime, { color: colors.textSecondary }]}>
          {startFormatted} - {endFormatted}
        </Text>
        <Text style={[styles.classTitle, { color: colors.text }]}>
          {t(entry.class_name, { defaultValue: entry.class_name }) || 'Class'}
        </Text>
        <Text style={[styles.classMeta, { color: colors.textTertiary }]}>
          {(entry.coaches || []).length > 0
            ? t('home.coachPrefix', { name: entry.coaches![0] })
            : t('home.coachTba')}{' '}
          {'•'} {t(entry.location_name || '', { defaultValue: entry.location_name }) || t('home.mainMat')}
        </Text>

        {/* Badges */}
        {showBadges && (
          <View style={styles.badgeRow}>
            <ClassStatusBadge
              isClosed={isClosed}
              isPublicHoliday={isPublicHoliday}
              closedReason={entry.closed_reason}
              isTimeChanged={isOverride}
            />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  classCard: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
  },
  classCardLeft: {
    flex: 1,
    gap: 4,
  },
  classTime: {
    fontSize: 13,
  },
  classTitle: {
    fontSize: 15,
    fontWeight: '700',
  },
  classMeta: {
    fontSize: 12,
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  // Compact variant styles
  compactCard: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
  },
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  timeBox: {
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    minWidth: 60,
  },
  timeLabel: {
    fontSize: 10,
    fontWeight: '600',
  },
  timeValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  closedLabel: {
    fontSize: 10,
    fontWeight: '600',
    marginTop: 2,
  },
  compactInfo: {
    flex: 1,
    gap: 4,
  },
  compactClassName: {
    fontSize: 16,
    fontWeight: '600',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 12,
  },
});
